import PropTypes from "prop-types";
import { useRef } from "react";
import { Link as RouterLink } from "react-router-dom";
import { formatDistanceToNow } from "date-fns";
import { Icon } from "@iconify/react";
import bellFill from "@iconify/icons-eva/bell-fill";
import clockFill from "@iconify/icons-eva/clock-fill";
import doneAllFill from "@iconify/icons-eva/done-all-fill";
import CircleIcon from "@mui/icons-material/Circle";
import GroupIcon from "@mui/icons-material/Group";
import PersonRemoveIcon from "@mui/icons-material/PersonRemove";
import NotificationsActiveIcon from "@mui/icons-material/NotificationsActive";
import HandshakeIcon from "@mui/icons-material/Handshake";
import RestaurantMenuIcon from "@mui/icons-material/RestaurantMenu";
import FitnessCenterIcon from "@mui/icons-material/FitnessCenter";
import CampaignIcon from "@mui/icons-material/Campaign";
import { alpha } from "@mui/material/styles";
import {
  Box,
  List,
  Badge,
  Avatar,
  Tooltip,
  Divider,
  IconButton,
  Typography,
  ListItemText,
  ListItemAvatar,
  ListItemButton,
} from "@mui/material";
// components
import Scrollbar from "../../components/Scrollbar";
import MenuPopover from "../../components/MenuPopover";
import {
  addGroup,
  approved,
  exit,
  group,
  newPost,
  pending,
  reject,
  heart,
  comment,
  coinNotification,
} from "src/assets";
import { markAllAsRead } from "src/DAL/Notification/Notification";

function renderContent(notification) {
  const title = (
    <Typography variant="subtitle2" sx={{ fontSize: "0.75rem" }}>
      {notification.title}
    </Typography>
  );

  if (notification.notification_type === "feed_like") {
    return {
      avatar: <img alt={notification.title} src={heart} />,
      title,
    };
  }
  if (notification.notification_type === "feed_comment_add") {
    return {
      avatar: <img alt={notification.title} src={comment} />,
      title,
    };
  }
  if (notification.notification_type === "feed_coins_share") {
    return {
      avatar: <img alt={notification.title} src={coinNotification} />,
      title,
    };
  }
  if (notification.notification_type === "member_left_group") {
    return {
      avatar: <img alt={notification.title} src={exit} />,
      title,
    };
  }
  if (notification.notification_type === "feed_added") {
    return {
      avatar: <img alt={notification.title} src={newPost} />,
      title,
    };
  }
  if (notification.notification_type === "feed_pending") {
    return {
      avatar: <img alt={notification.title} src={pending} />,
      title,
    };
  }
  if (notification.notification_type === "feed_approved") {
    return {
      avatar: <img alt={notification.title} src={approved} />,
      title,
    };
  }
  if (notification.notification_type === "feed_rejected") {
    return {
      avatar: <img alt={notification.title} src={reject} />,
      title,
    };
  }
  if (notification.notification_type === "member_removed_from_group") {
    return {
      avatar: <img alt={notification.title} src={group} />,
      title,
    };
  }
  if (notification.notification_type === "member_added_to_group") {
    return {
      avatar: <img alt={notification.title} src={addGroup} />,
      title,
    };
  }
  if (notification.notification_type === "friend_request") {
    return {
      avatar: <GroupIcon style={{ fill: "#000" }} />,
      title,
    };
  }
  if (notification.notification_type === "friend_request_acceptance") {
    return {
      avatar: <GroupIcon style={{ fill: "#000" }} />,
      title,
    };
  }
  if (notification.notification_type === "friend_request_rejection") {
    return {
      avatar: <PersonRemoveIcon style={{ fill: "#000" }} />,
      title,
    };
  }
  if (notification.notification_type === "accountability_partner_request") {
    return {
      avatar: <HandshakeIcon style={{ fill: "#000" }} />,
      title,
    };
  }
  if (notification.notification_type === "accountability_request_acceptance") {
    return {
      avatar: <HandshakeIcon style={{ fill: "#000" }} />,
      title,
    };
  }
  if (notification.notification_type === "deficit_off_notification") {
    return {
      avatar: <RestaurantMenuIcon style={{ fill: "#000" }} />,
      title,
    };
  }

  if (notification.notification_type === "WEIGHT_REMINDER") {
    return {
      avatar: <FitnessCenterIcon style={{ fill: "#000" }} />,
      title,
    };
  }

  if (notification.notification_type === "general_admin") {
    return {
      avatar: <CampaignIcon style={{ fill: "#000" }} />,
      title: (
        <>
          {title}
          {notification.description && (
            <Typography
              variant="body2"
              component="span"
              sx={{
                display: "block",
                fontSize: "0.75rem",
                color: "text.secondary",
                whiteSpace: "pre-line",
                wordBreak: "break-word",
              }}
            >
              {notification.description}
            </Typography>
          )}
        </>
      ),
    };
  }

  return {
    avatar: <NotificationsActiveIcon style={{ fill: "#000" }} />,
    title,
  };
}

export default function NotificationsPopover({
  allNotifications,
  setUnreadNotificationCount,
  unreadNotificationCount,
  setAllNotifications,
  handleRedirect,
  setOpen,
  open,
}) {
  const anchorRef = useRef(null);

  const handleOpen = () => {
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
  };

  NotificationItem.propTypes = {
    notification: PropTypes.object.isRequired,
  };
  function NotificationItem({ notification }) {
    const { avatar, title } = renderContent(notification);

    return (
      <ListItemButton
        to="#"
        disableGutters
        component={RouterLink}
        sx={{
          py: 1.5,
          px: 1.0,
          mt: "1px",
          pr: 1.4,

          ...(!notification.is_read && {
            bgcolor: "action.selected",
            color: "#8a8a8a",
          }),
        }}
        onClick={(e) => {
          e.preventDefault();
          handleRedirect(
            notification._id,
            notification.notification_type,
            notification,
          );
        }}
      >
        {!notification.is_read && (
          <CircleIcon
            className={`notifications-circle ${
              notification.is_read ? "notifications-circle-seen" : "ml-5"
            }`}
          />
        )}

        <ListItemAvatar>
          <Avatar
            sx={{
              bgcolor: "background.neutral",
              ...(notification.is_read && {
                ml: "12px",
              }),
            }}
          >
            {avatar}
          </Avatar>
        </ListItemAvatar>
        <ListItemText
          sx={{
            color: "#1b1b1b",
          }}
          primary={title}
          secondary={
            <Typography
              variant="caption"
              sx={{
                mt: 0.5,
                display: "flex",
                alignItems: "center",
                color: "text.disabled",
              }}
            >
              <Box
                component={Icon}
                icon={clockFill}
                sx={{ mr: 0.5, width: 16, height: 16 }}
              />
              {formatDistanceToNow(new Date(notification.createdAt))} ago
            </Typography>
          }
        />
      </ListItemButton>
    );
  }
  const handleMarkAllAsRead = () => {
    setUnreadNotificationCount(0);
    markAllAsRead(); //api to mark all read
    setAllNotifications(
      allNotifications.map((notification) => ({
        ...notification,
        is_read: true,
      })),
    );
  };
  return (
    <>
      <IconButton
        ref={anchorRef}
        size="large"
        color={open ? "primary" : "default"}
        onClick={handleOpen}
        sx={{
          ...(open && {
            bgcolor: (theme) =>
              alpha(
                theme.palette.primary.main,
                theme.palette.action.focusOpacity,
              ),
          }),
        }}
      >
        <Badge badgeContent={unreadNotificationCount} color="error">
          <Icon icon={bellFill} width={20} height={20} />
        </Badge>
      </IconButton>

      <MenuPopover
        open={open}
        onClose={handleClose}
        anchorEl={anchorRef.current}
        sx={{ width: 360 }}
      >
        <Box sx={{ display: "flex", alignItems: "center", py: 2, px: 2.5 }}>
          <Box sx={{ flexGrow: 1 }}>
            <Typography variant="subtitle1">Notifications</Typography>
            <Typography variant="body2" sx={{ color: "text.secondary" }}>
              You have {unreadNotificationCount} unread notifications.
            </Typography>
          </Box>

          {unreadNotificationCount > 0 && (
            <Tooltip title=" Mark all as read">
              <IconButton color="primary" onClick={handleMarkAllAsRead}>
                <Icon icon={doneAllFill} width={20} height={20} />
              </IconButton>
            </Tooltip>
          )}
        </Box>

        <Divider />

        <Scrollbar sx={{ height: { xs: 340, sm: "auto" } }}>
          <List disablePadding>
            {allNotifications.map((notification, index) => (
              <NotificationItem key={index} notification={notification} />
            ))}
          </List>
        </Scrollbar>
      </MenuPopover>
    </>
  );
}
